import { CHECKOUT_TYPE } from "@/components/Checkout/constants.checkout";
import { useReactivatePlanMutation } from "@/components/Checkout/hooks/mutations/useReactivatePlanMutation";
import { useResumePlanMutation } from "@/components/Checkout/hooks/mutations/useResumePlanMutation";
import { useStartCancellation } from "@/components/Checkout/hooks/useStartCancellation";
import { useUserPlanDerived } from "@/components/Checkout/hooks/useUserPlanDerived";
import { buildSwitchIntervalCopy } from "@/components/Checkout/utils/switch-interval-copy";
import {
  TAddOn,
  TAddOnRules,
  TPlan,
} from "@/components/Checkout/types.checkout";
import { oldPlanNewPlanNameMapping } from "@/components/PlanMigration/data";
import { PLAN_REF_ID_MAPPING_V1, PLAN_STATUS } from "@/data/plans";
import useHandleBookACall from "@/hooks/useHandleBookACall";
import { useScrollToId } from "@/hooks/useScrollToId";
import useGetUserToken from "@/lib/auth/useGetUserToken";
import { useNavigate } from "@/lib/router";
import { TUserPlanData } from "@/types/plan-data";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { useIntercom } from "react-use-intercom";
import { useUser } from "reactfire";

import { BILLING_INTERVAL, DEFAULT_CURRENCY } from "@/data/constants";
import { isYearlyBilledMonthly } from "@/utils/billingInterval";
import derivePlanIntervalType from "@/utils/derivePlanIntervalType";
import { DASHBOARD_EVENTS } from "./events.dashboard";
import { DashboardService } from "./service.dashboard";
import { resolvePlanActionType } from "./utils";

interface UseDashboardProps {
  userPlanData?: TUserPlanData;
  userPlanDataLoading: boolean;
  planCards: TPlan[];
  addOnCards: TAddOn[];
  addOnRules: TAddOnRules[];
}

/**
 * **Facade hook** for the Dashboard module.
 *
 * Assumes {@link DashboardService} has already been initialized by `index.tsx`.
 * Extracts all business logic from the render layer and returns a flat object
 * consumed by {@link DashboardContent}.
 */
export function useDashboard({
  userPlanData,
  userPlanDataLoading,
  planCards,
  addOnCards,
  addOnRules,
}: UseDashboardProps) {
  const { getQueryParam, track } = DashboardService;
  const navigate = useNavigate();

  const { data: userData } = useUser();
  const { handleBookACall } = useHandleBookACall();
  const scrollToId = useScrollToId();
  const userToken = useGetUserToken();
  const { showNewMessage } = useIntercom();

  // ── Derived plan state ───────────────────────────────────────────────────

  const {
    planStatus,
    isPlanActive,
    IS_USER_ON_PRICING_V2,
    isPlanCancelling: hasUserCancelled,
    cancelAt,
    resumeAt,
    currentPeriodEnd,
    totalCustomAiProfiles,
    totalInstantAiProfiles,
    ownedPlanList,
    ownedAddOnItemList,
    rawUserProductIds,
    v1PlanList,
    v1AddOnList,
    planPriceAmount,
    hasGalleryUnlimitedAddOn,
  } = useUserPlanDerived(userPlanData, planCards, addOnCards);

  const reactivateMutation = useReactivatePlanMutation({
    isV1: !IS_USER_ON_PRICING_V2,
    onSuccessCallback: () => {
      toast.success("Reactivated successfully");
    },
  });

  const resumeMutation = useResumePlanMutation();

  const { productRefId: rawUserProductIdV1, recurring } =
    userPlanData ?? ({} as TUserPlanData);

  const currentBillingInterval = derivePlanIntervalType(recurring);
  const billingPeriod = currentBillingInterval ?? BILLING_INTERVAL.YEARLY;

  const { handleNavigateToCancel } = useStartCancellation({
    planCards,
    ownedPlanList,
    userToken,
    billingPeriod,
    ownedPlanCount: ownedPlanList.length,
    isV2: IS_USER_ON_PRICING_V2,
  });

  const isPlanOnTrial = planStatus === PLAN_STATUS.TRIALING;
  const isUserOnYearlyPlan = currentBillingInterval === BILLING_INTERVAL.YEARLY;
  const isOnYearlyBilledMonthly = isYearlyBilledMonthly(currentBillingInterval);
  const isV1User = !IS_USER_ON_PRICING_V2 && !!rawUserProductIdV1;
  const isMaxPlan = rawUserProductIdV1 === PLAN_REF_ID_MAPPING_V1.max;
  const planActionType = resolvePlanActionType({
    planStatus,
    cancelAt,
    isV1User,
    isMaxPlan,
    isCancelOnly: isOnYearlyBilledMonthly,
  });

  const isFounder = !!userPlanData?.isFounder;

  const activePlanNames = (() => {
    const names =
      !IS_USER_ON_PRICING_V2 && rawUserProductIdV1
        ? [oldPlanNewPlanNameMapping[rawUserProductIdV1]]
        : ownedPlanList.map((plan) => plan.planName);

    if (!isFounder) return names;

    const tobeChangeList = ["Complete Workflow", "Max", "Complete"];

    const renewLabel = planPriceAmount
      ? ` (renew at $${planPriceAmount.price_in_dollars} ${planPriceAmount.currency.toUpperCase()})`
      : "";

    return names.map((name) =>
      tobeChangeList.includes(name) ? `Founders${renewLabel}` : name,
    );
  })();

  const isEmailVerified = !!userData?.emailVerified;
  const hasSubscription = !!userPlanData && activePlanNames.length > 0;
  const showDownloadLinks = isPlanOnTrial || !userPlanData;
  const showYearlyBanner = !isUserOnYearlyPlan && isPlanActive;

  const switchIntervalCopy = buildSwitchIntervalCopy({
    ownedPlanList,
    ownedAddOnItemList,
    addOnCards,
    currency: ownedPlanList[0]?.pricing.currency ?? DEFAULT_CURRENCY,
    renewsAt: DashboardService.Content.switchIntervalCard.renewsAt,
    isFounder,
    planPriceAmount,
  });
  const showProductList = !isOnYearlyBilledMonthly;

  // ── Subscription handlers ────────────────────────────────────────────────

  function handleCancelPlan() {
    handleNavigateToCancel();
  }

  function handleContactSupport() {
    showNewMessage();
  }

  function handlePlanManage() {
    if (rawUserProductIdV1 === PLAN_REF_ID_MAPPING_V1.max) {
      navigate("/review");
      return;
    }

    navigate(`/checkout?type=${CHECKOUT_TYPE.MANAGE}&billing=${billingPeriod}`);

    track(DASHBOARD_EVENTS.MANAGE_CTA, {
      pricing_version: "v2",
      billing_period: billingPeriod,
      plan_names: activePlanNames,
      plan_status: planStatus,
    });
  }

  function handlePlanPurchase() {
    const planParam = ownedPlanList.map((p) => p.productRefId).join(",");
    const planQuery = planParam ? `&plan=${planParam}` : "";

    navigate(
      `/checkout?type=${CHECKOUT_TYPE.PURCHASE}&billing=${billingPeriod}${planQuery}`,
    );

    //
    track(DASHBOARD_EVENTS.PURCHASE_CTA, {
      pricing_version: "v2",
      billing_period: billingPeriod,
      plan_names: activePlanNames,
      plan_status: planStatus,
    });
  }

  function handleMigration() {
    navigate(
      `/checkout?type=${CHECKOUT_TYPE.MIGRATION}&cancellation=true&pause=true`,
    );

    track(DASHBOARD_EVENTS.MANAGE_CTA, {
      pricing_version: "v2",
      billing_period: billingPeriod,
      plan_names: activePlanNames,
      plan_status: planStatus,
    });
  }

  function handleReactivate() {
    reactivateMutation.mutate();

    track(DASHBOARD_EVENTS.REACTIVATE_CTA, {
      pricing_version: "v2",
      billing_period: billingPeriod,
      plan_names: activePlanNames,
      plan_status: planStatus,
    });
  }

  function handleResume() {
    resumeMutation.mutate(rawUserProductIds);
    track(DASHBOARD_EVENTS.RESUME_CTA, {
      pricing_version: "v2",
      billing_period: billingPeriod,
      plan_names: activePlanNames,
      plan_status: planStatus,
    });
  }

  function handlePaymentDetailsClick() {
    scrollToId("payment-and-billing");

    track(DASHBOARD_EVENTS.PAYMENT_DETAILS, {
      plan_status: planStatus,
      billing_period: billingPeriod,
    });
  }

  // ── Intercom side-effect ─────────────────────────────────────────────────

  const shouldOpenIntercom = getQueryParam("open-intercom") === "true";

  useEffect(() => {
    if (shouldOpenIntercom) {
      handleBookACall();
    }
  }, [shouldOpenIntercom]);

  // ── Return flat object ───────────────────────────────────────────────────

  return {
    // plan state
    UserPlan: {
      planStatus,
      isPlanActive,
      isPlanOnTrial,
      isUserOnYearlyPlan,
      IS_USER_ON_PRICING_V2,
      hasUserCancelled,
      cancelAt,
      resumeAt,
      currentPeriodEnd,
      totalCustomAiProfiles,
      totalInstantAiProfiles,
      billingInterval: currentBillingInterval,
      billingPeriod,
      activePlanNames,
      isFounder,
      ownedPlanList,
      ownedAddOnItemList,
      rawUserProductIds,
      v1PlanList,
      v1AddOnList,
      hasGalleryUnlimitedAddOn,
    },
    // plan action
    planActionType,
    // UI flags
    isEmailVerified,
    hasSubscription,
    showDownloadLinks,
    showYearlyBanner,
    switchIntervalCopy,
    showProductList,
    isLoading: userPlanDataLoading,
    isReactivating: reactivateMutation.isPending,
    isResuming: resumeMutation.isPending,
    // data pass-through
    planCards,
    addOnCards,
    addOnRules,
    // handlers
    Handlers: {
      handlePaymentDetailsClick,
      handlePlanManage,
      handlePlanPurchase,
      handleReactivate,
      handleResume,
      handleMigration,
      handleCancelPlan,
      handleContactSupport,
    },
  };
}

export type TDashboardHandlers = ReturnType<typeof useDashboard>["Handlers"];
export type TDashboardUserPlan = ReturnType<typeof useDashboard>["UserPlan"];
